import React from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { ModerationAttendeeComment } from '../../../../../../models';
import useModerationContext from '../../../../../../context/useModerationContext';

import { CommentCard, ToastContext } from '@weezevent/nacre';
import LoadingCard from '../../../../../../components/loadingCard';

import Style from '../../moderationProfile.module.css';

export const ModerationCommentBlock = ({ organization, attendee }) => {
    const { t } = useTranslation();
    const locale = i18n.language;
    const toast = React.useContext(ToastContext);
    const [{ flow, removedAttendeesQuery }] = useModerationContext();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [comments, loadingComments, syncComments] = ModerationAttendeeComment.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId: organization.id,
            attendeeId: attendee.id,
            query: attendee?.removed ? removedAttendeesQuery : {}
        },
        [organization.id, attendee, removedAttendeesQuery]
    );

    const loading = React.useMemo(() => loadingComments || isSubmitting, [loadingComments, isSubmitting]);

    const commentItems = React.useMemo(() => {
        if (!comments || loading) {
            return [];
        }
        return comments.map(comment => ({
            id: comment.uuid,
            date: comment.created,
            text: comment.comment,
            user: comment.resolved_user_name
        }));
    }, [comments, loading]);

    const onSubmit = React.useCallback(
        value => {
            setIsSubmitting(true);
            ModerationAttendeeComment.create({ organizationId: organization.id, attendeeId: attendee.id, flow, comment: value })
                .then(() => {
                    toast.success(t(`ticket.manager.attendees.detail.comment.saveSucess`));
                })
                .catch(_ => {
                    toast.error(t(`ticket.manager.attendees.detail.comment.saveError`));
                })
                .finally(() => {
                    setIsSubmitting(false);
                    syncComments();
                });
        },
        [organization.id, attendee.id, flow]
    );

    return (
        <div className={Style['attendee-card-comment']}>
            <CommentCard
                items={commentItems}
                onSubmit={onSubmit}
                loading={loading ? <LoadingCard loading={true} /> : false}
                locale={locale || 'en'}
                tradDelete={t('ticket.common.action.delete')}
                tradEdit={t('ticket.common.action.edit')}
                tradPlaceholder={t('ticket.moderation.attendee.comments.addComment')}
                tradEmpty={t('ticket.moderation.attendee.comments.emptyTitle')}
                tradTitle={t('ticket.moderation.attendee.comments.title')}
                tradViewAll={t('ticket.moderation.attendee.comments.seeAll')}
                tradViewMore={t('ticket.common.action.next')}
            />
        </div>
    );
};
